import React from "react";
import { Capacitor } from "@capacitor/core";
import { Contacts } from "@capacitor-community/contacts";
import { useLanguage } from "../../i18n";

const isNative = Capacitor.isNativePlatform();

// The Web Contact Picker API (navigator.contacts.select) only exists on
// Android Chrome in a real browser tab — it's never implemented inside a
// WebView, which is what the native app actually runs in, so this always
// returns unsupported there. The native app instead uses the Capacitor
// Contacts plugin (below), which talks to Android's real contacts provider.
function isWebPickerSupported(): boolean {
  return typeof navigator !== "undefined" && typeof (navigator as any).contacts?.select === "function";
}

// Strips everything but digits and keeps the last 10 — Indian mobile numbers
// come back from the picker in all sorts of shapes ("+91 98765 43210",
// "098765-43210"), but the rest of the app expects a bare 10-digit number.
function cleanPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  return digits.slice(-10);
}

async function pickNative(): Promise<{ name: string; phone: string } | null> {
  const perm = await Contacts.checkPermissions();
  if (perm.contacts !== "granted") {
    const requested = await Contacts.requestPermissions();
    if (requested.contacts !== "granted") return null;
  }
  const { contact } = await Contacts.pickContact({ projection: { name: true, phones: true } });
  const name = contact.name?.display ?? "";
  const rawPhone = contact.phones?.[0]?.number ?? "";
  return { name, phone: rawPhone ? cleanPhone(rawPhone) : "" };
}

async function pickWeb(): Promise<{ name: string; phone: string } | null> {
  const contacts = await (navigator as any).contacts.select(["name", "tel"], { multiple: false });
  const contact = contacts?.[0];
  if (!contact) return null;
  const name = contact.name?.[0] ?? "";
  const phone = contact.tel?.[0] ? cleanPhone(contact.tel[0]) : "";
  return { name, phone };
}

export function ContactPickerButton({ onPick }: { onPick: (contact: { name: string; phone: string }) => void }) {
  const { t } = useLanguage();
  if (!isNative && !isWebPickerSupported()) return null;

  async function pick() {
    try {
      const result = isNative ? await pickNative() : await pickWeb();
      if (result) onPick(result);
    } catch {
      // user cancelled the picker, or permission was denied — nothing to do
    }
  }

  return (
    <button type="button" className="contact-pick-btn" onClick={pick}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
      {t("assignTenantForm.addFromContacts")}
    </button>
  );
}
